import { useCallback, useEffect, useState } from "react";

import analyticsService from "../api/analytics.service";

import {
  AnalyticsDashboard,
  AnalyticsFilters,
  AnalyticsFilterOption,
  DashboardAnalytics,
  InventoryAnalytics,
  LowStockItem,
  MaintenanceAnalytics,
  OperationsAnalytics,
  ProcurementAnalytics,
  PurchaseAnalytics,
  RecentActivity,
  ReportsAnalytics,
  SupplierAnalytics,
  TopMovingPart,
} from "../types";

const defaultFilters: AnalyticsFilters = {};

export const useAnalytics = () => {
  const [rawDashboard, setRawDashboard] =
    useState<DashboardAnalytics | null>(null);
  const [dashboard, setDashboard] = useState<AnalyticsDashboard | null>(null);
  const [inventory, setInventory] = useState<InventoryAnalytics | null>(null);
  const [maintenance, setMaintenance] =
    useState<MaintenanceAnalytics | null>(null);
  const [operations, setOperations] = useState<OperationsAnalytics | null>(null);
  const [reports, setReports] = useState<ReportsAnalytics | null>(null);
  const [purchase, setPurchase] = useState<PurchaseAnalytics | null>(null);
  const [supplier, setSupplier] = useState<SupplierAnalytics | null>(null);
  const [topMovingParts, setTopMovingParts] = useState<TopMovingPart[]>([]);
  const [lowStockItems, setLowStockItems] = useState<LowStockItem[]>([]);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [filterOptions, setFilterOptions] = useState<{
    hubs: AnalyticsFilterOption[];
    categories: AnalyticsFilterOption[];
    suppliers: AnalyticsFilterOption[];
  }>({
    hubs: [],
    categories: [],
    suppliers: [],
  });
  const [filters, setFilters] = useState<AnalyticsFilters>(defaultFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(
    async (currentFilters: AnalyticsFilters) => {
      try {
        setLoading(true);
        setError(null);

        const [
          dashboardData,
          inventoryData,
          procurementData,
          maintenanceData,
          operationsData,
          reportsData,
        ] = await Promise.all([
          analyticsService.getDashboardAnalytics(currentFilters),
          analyticsService.getInventoryAnalytics(currentFilters),
          analyticsService.getProcurementAnalytics(currentFilters),
          analyticsService.getMaintenanceAnalytics(currentFilters),
          analyticsService.getOperationsAnalytics(currentFilters),
          analyticsService.getReportsAnalytics(currentFilters),
        ]);

        setRawDashboard(dashboardData);
        setInventory(inventoryData);
        setMaintenance(maintenanceData);
        setOperations(operationsData);
        setReports(reportsData);
        setDashboard(
          analyticsService.toDashboardView(
            dashboardData,
            procurementData,
            maintenanceData,
            reportsData
          )
        );
        setPurchase(analyticsService.toPurchaseView(procurementData));
        setSupplier(analyticsService.toSupplierView(procurementData));
        setTopMovingParts(analyticsService.toTopMovingParts(inventoryData));
        setLowStockItems(analyticsService.toLowStockItems(inventoryData));
        setRecentActivities(analyticsService.toRecentActivities(reportsData));
      } catch (err) {
        console.error(err);
        setError("Failed to load analytics dashboard.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchAnalytics(defaultFilters);
  }, [fetchAnalytics]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [hubs, categories, suppliers] = await Promise.all([
          analyticsService.getHubs(),
          analyticsService.getCategories(),
          analyticsService.getSuppliers(),
        ]);

        setFilterOptions({
          hubs,
          categories,
          suppliers,
        });
      } catch (err) {
        console.error(err);
      }
    };

    fetchFilterOptions();
  }, []);

  const refresh = async () => {
    await fetchAnalytics(filters);
  };

  const updateFilters = (newFilters: Partial<AnalyticsFilters>) => {
    const updatedFilters = {
      ...filters,
      ...newFilters,
    };

    setFilters(updatedFilters);
    fetchAnalytics(updatedFilters);
  };

  const exportReport = async () => {
    const blob = new Blob([JSON.stringify(reports ?? {}, null, 2)], {
      type: "application/json",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `spare-parts-analytics-${new Date()
      .toISOString()
      .split("T")[0]}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return {
    rawDashboard,
    dashboard,
    inventory,
    maintenance,
    operations,
    reports,
    purchase,
    supplier,
    topMovingParts,
    lowStockItems,
    recentActivities,
    filterOptions,
    filters,
    loading,
    error,
    refresh,
    fetchAnalytics,
    updateFilters,
    exportReport,
  };
};

export default useAnalytics;
